import React, { useState } from 'react';
import { TaskFile } from '../../types';
import { PaperclipIcon, PlusIcon, ImageIcon, FileIcon } from '../../components/icons/Icons';
import { FILE_PATTERNS } from '../../constants';
import { formatFileSize } from '../../lib/storageHelpers';

interface AttachmentsCardProps {
  attachments: TaskFile[];
  isProjectManager: boolean;
  onAddAttachment: () => void;
  onRemoveAttachment: (attId: string) => void;
  onPreviewAttachment: (att: TaskFile) => void;
  onDropFiles?: (files: File[]) => void;
}

export const AttachmentsCard: React.FC<AttachmentsCardProps> = ({
  attachments,
  isProjectManager,
  onAddAttachment,
  onRemoveAttachment,
  onPreviewAttachment,
  onDropFiles,
}) => {
  const [dragOver, setDragOver] = useState(false);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (!onDropFiles) return;
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) onDropFiles(files);
  };

  const getAttachmentThumbnail = (att: TaskFile) => {
    const isImg = FILE_PATTERNS.IMAGE.test(att.fileName);
    const isPdf = FILE_PATTERNS.PDF.test(att.fileName);
    const hasUrl = att.publicUrl && att.publicUrl.startsWith('data:');
    if (isImg || hasUrl) {
      return (
        <button type="button" className="att-thumb att-thumb-img" onClick={() => onPreviewAttachment(att)} title={`Preview ${att.fileName}`}>
          {att.publicUrl ? (
            <img src={att.publicUrl} alt={att.fileName} />
          ) : (
            <ImageIcon size={24} />
          )}
        </button>
      );
    }
    if (isPdf) {
      return (
        <button type="button" className="att-thumb att-thumb-pdf" onClick={() => onPreviewAttachment(att)} title={`Preview ${att.fileName}`}>
          <FileIcon size={24} className="icon-red" />
        </button>
      );
    }
    return (
      <button type="button" className="att-thumb att-thumb-file" onClick={() => onPreviewAttachment(att)} title={`Preview ${att.fileName}`}>
        <FileIcon size={24} />
      </button>
    );
  };

  return (
    <div
      className={`detail-card full-width${dragOver ? ' att-drag-over' : ''}`}
      onDrop={handleDrop}
      onDragOver={e => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
    >
      <h3 className="detail-card-title">
        <PaperclipIcon size={16} />
        Attachments ({attachments.length})
        {isProjectManager && (
          <button className="action-icon-btn add ml-auto" onClick={onAddAttachment} title="Add attachment">
            <PlusIcon size={14} />
          </button>
        )}
      </h3>
      {attachments.length > 0 ? (
        <div className="att-grid">
          {attachments.map(att => (
            <div key={att.id} className="att-card">
              {getAttachmentThumbnail(att)}
              <span className="att-name" title={att.fileName}>{att.fileName}</span>
              {att.fileSize > 0 && (
                <span className="att-size">{formatFileSize(att.fileSize)}</span>
              )}
              {isProjectManager && (
                <button className="att-remove-btn" onClick={() => onRemoveAttachment(att.id)} title="Remove">&#10005;</button>
              )}
            </div>
          ))}
        </div>
      ) : (
        <p className="task-no-history">
          {dragOver ? 'Drop files here...' : 'No attachments yet. Drag & drop files or click + to add.'}
        </p>
      )}
    </div>
  );
};
